import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Container,
  Chip,
  Alert,
  Link,
} from '@mui/material';
import {
  People as PeopleIcon,
  Event as EventIcon,
  Message as MessageIcon,
  TrendingUp as TrendingUpIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  SmartToy as SmartToyIcon,
  History as HistoryIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { MagicBackground } from '../components/MagicBackground';
import { Vendor } from '../types';
import { useBusiness } from '../contexts/BusinessContext';
import CreateBusinessDialog from '../components/CreateBusinessDialog';

interface DashboardStats {
  totalCustomers: number;
  activeCustomers: number;
  upcomingAppointments: number;
  activeConversations: number;
  weeklyGrowth: number;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [spendingData, setSpendingData] = useState<any>(null);
  const [recentConversations, setRecentConversations] = useState<any[]>([]);
  const [totalMessages, setTotalMessages] = useState(0);

  // Use global business context
  const { hasNoBusinesses, refreshVendors, switchBusiness } = useBusiness();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    // If user has no businesses, we don't need to load dashboard data
    if (hasNoBusinesses) {
      setLoading(false);
      return;
    }
    
    // Otherwise load data
    loadDashboardData();
  }, [hasNoBusinesses]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Initialize stats with defaults to prevent undefined errors
      let newStats: DashboardStats = {
        totalCustomers: 0,
        activeCustomers: 0,
        upcomingAppointments: 0,
        activeConversations: 0,
        weeklyGrowth: 0,
      };
      
      // Load vendor information
      try {
        const vendorData = await apiService.getVendor();
        setVendor(vendorData);
        // Load spending data if phone is activated
        if (vendorData?.phone) {
          try {
            const spending = await apiService.getSMSSpending();
            setSpendingData(spending);
          } catch (error) {
            console.error('Error loading spending data:', error);
          }
        }
      } catch (error) {
        console.error('Error loading vendor data:', error);
      }

      // Load customers with error handling
      let customers: any[] = [];
      try {
        customers = await apiService.getCustomers();
        const activeCustomers = customers.filter(c => c.status === 'active');
        newStats.totalCustomers = customers.length;
        newStats.activeCustomers = activeCustomers.length;
      } catch (error) {
        console.error('Error loading customers:', error);
      }

      // Load conversations to get real active conversation count and recent activity
      try {
        const conversations = await apiService.getConversations({ status: 'active' });
        newStats.activeConversations = conversations.length;
        
        // Get recent conversations (last 5, sorted by lastMessageAt)
        const recent = [...conversations]
          .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime())
          .slice(0, 5);
        setRecentConversations(recent);
        
        // Calculate total messages from conversations
        const totalMsg = conversations.reduce((sum, conv) => sum + (conv.messageCount || 0), 0);
        setTotalMessages(totalMsg);
      } catch (error) {
        console.error('Error loading conversations:', error);
        // Fallback: use active customers count if conversations fail
        newStats.activeConversations = newStats.activeCustomers;
        setRecentConversations([]);
        setTotalMessages(0);
      }

      // Load appointments with error handling
      let upcoming: any[] = [];
      try {
        const today = new Date().toISOString().split('T')[0];
        const appointments = await apiService.getAppointments({ date: today });
        upcoming = appointments.filter(
          a => a.status === 'scheduled' || a.status === 'confirmed'
        );
        newStats.upcomingAppointments = upcoming.length;
      } catch (error) {
        console.error('Error loading appointments:', error);
      }

      // Calculate weekly growth
      try {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const newCustomersThisWeek = customers.filter(
          c => c.createdAt && new Date(c.createdAt) >= weekAgo
        ).length;
        const previousWeekStart = new Date(weekAgo);
        previousWeekStart.setDate(previousWeekStart.getDate() - 7);
        const newCustomersPreviousWeek = customers.filter(
          c => c.createdAt && new Date(c.createdAt) >= previousWeekStart && new Date(c.createdAt) < weekAgo
        ).length;
        newStats.weeklyGrowth = newCustomersPreviousWeek > 0 
          ? Math.round(((newCustomersThisWeek - newCustomersPreviousWeek) / newCustomersPreviousWeek) * 100 * 10) / 10
          : newCustomersThisWeek > 0 ? 100 : 0;
      } catch (error) {
        console.error('Error calculating weekly growth:', error);
      }

      // Always set stats, even if some data failed to load
      setStats(newStats);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Ensure stats are set to defaults even on error
      setStats({
        totalCustomers: 0,
        activeCustomers: 0,
        upcomingAppointments: 0,
        activeConversations: 0,
        weeklyGrowth: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    gradient: string;
  }> = ({ title, value, icon, gradient }) => (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${gradient})`,
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 3,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
        },
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          {icon}
        </Box>
        <Typography variant="h2" fontWeight="bold" color="white" sx={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
          {value}
        </Typography>
        <Typography variant="body1" color="rgba(255,255,255,0.9)" mt={1} fontWeight={500}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  // Ensure stats is always defined - use useMemo to prevent recalculation issues
  const safeStats = useMemo(() => {
    if (!stats) {
      return {
        totalCustomers: 0,
        activeCustomers: 0,
        upcomingAppointments: 0,
        activeConversations: 0,
        weeklyGrowth: 0,
      };
    }
    return {
      totalCustomers: stats.totalCustomers ?? 0,
      activeCustomers: stats.activeCustomers ?? 0,
      upcomingAppointments: stats.upcomingAppointments ?? 0,
      activeConversations: stats.activeConversations ?? 0,
      weeklyGrowth: stats.weeklyGrowth ?? 0,
    };
  }, [stats]);

  const handleCreateBusinessSuccess = (newVendor: Vendor) => {
    // Refresh vendor list via context
    refreshVendors().then(() => {
      // Switch to new business
      switchBusiness(newVendor.vendorId);
      // Close dialog
      setCreateDialogOpen(false);
    });
  };

  // Show welcome screen if no businesses exist
  if (hasNoBusinesses) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <MagicBackground />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box mb={4}>
            <Box 
              sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                bgcolor: 'rgba(123, 97, 255, 0.1)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3,
                border: '1px solid rgba(123, 97, 255, 0.3)',
              }}
            >
              <BusinessIcon sx={{ fontSize: 40, color: '#7B61FF' }} />
            </Box>
            <Typography variant="h3" fontWeight="bold" color="white" gutterBottom>
              Welcome to ReachOut!
            </Typography>
            <Typography variant="h6" color="rgba(255,255,255,0.7)" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
              To get started, please create your first business. You'll be able to manage customers, appointments, and automate communications.
            </Typography>
            
            <Box 
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                p: 4,
                border: '2px dashed rgba(255,255,255,0.2)',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#7B61FF',
                  bgcolor: 'rgba(123, 97, 255, 0.05)',
                  transform: 'translateY(-2px)',
                }
              }}
            >
              <AddIcon sx={{ fontSize: 48, color: '#7B61FF', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" color="white">
                Create New Business
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.5)" mt={1}>
                Click here to set up your business profile
              </Typography>
            </Box>
          </Box>
        </Container>

        <CreateBusinessDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={handleCreateBusinessSuccess}
        />
      </Box>
    );
  }

  if (loading || !safeStats) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <MagicBackground />
        <CircularProgress sx={{ color: '#667eea', position: 'relative', zIndex: 1 }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
      minHeight: '100vh', 
      position: 'relative',
      py: 4 
    }}>
      <MagicBackground />
      
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box mb={6}>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Typography 
              variant="h3" 
              fontWeight="bold" 
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Dashboard
            </Typography>
            {vendor?.isDemo && (
              <Chip
                label="Demo Business"
                sx={{
                  bgcolor: 'rgba(123, 97, 255, 0.2)',
                  color: '#7B61FF',
                  border: '1px solid rgba(123, 97, 255, 0.3)',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          <Typography variant="h6" color="rgba(255,255,255,0.7)">
            Welcome back! Here's an overview of your business.
          </Typography>
        </Box>

        {/* Phone Number Management Section */}
        {vendor && (
          <Box mb={4}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                p: 3,
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <PhoneIcon sx={{ fontSize: 32, color: '#00D4FF' }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold" color="white">
                      Phone Number
                    </Typography>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Your Phone Number
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {vendor.phone && (
                <Box>
                  <Alert 
                    severity="success" 
                    sx={{ 
                      bgcolor: 'rgba(67, 233, 123, 0.1)', 
                      color: '#43e97b',
                      border: '1px solid rgba(67, 233, 123, 0.2)',
                      mb: 1,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#43e97b',
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                          },
                        }}
                      />
                      <strong>Phone Number Live</strong>
                    </Box>
                  </Alert>
                  <Alert 
                    severity="info" 
                    sx={{ 
                      bgcolor: 'rgba(0, 212, 255, 0.1)', 
                      color: '#00D4FF',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                    }}
                  >
                    Your phone number: <strong>{vendor.phone}</strong> - Customers can text this number to chat with your AI agent
                  </Alert>
                </Box>
              )}
              {!vendor.phone && (
                <Alert 
                  severity="warning" 
                  sx={{ 
                    bgcolor: 'rgba(255, 193, 7, 0.1)', 
                    color: '#FFC107',
                    border: '1px solid rgba(255, 193, 7, 0.2)',
                  }}
                >
                  Phone number not activated. Go to Settings to activate your phone number.
                </Alert>
              )}
              {spendingData && vendor.phone && (
                <Box mt={2} p={2} sx={{ bgcolor: 'rgba(0, 0, 0, 0.2)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="white" fontWeight="bold" mb={1}>
                    SMS Spending (This Month)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="rgba(255,255,255,0.7)">Sent</Typography>
                      <Typography variant="body2" color="white">{spendingData.messagesSent}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="rgba(255,255,255,0.7)">Received</Typography>
                      <Typography variant="body2" color="white">{spendingData.messagesReceived}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="rgba(255,255,255,0.7)">Total</Typography>
                      <Typography variant="body2" color="#00D4FF" fontWeight="bold">${spendingData.spending.total.toFixed(2)}</Typography>
                    </Grid>
                    {spendingData.spendingLimit && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="rgba(255,255,255,0.7)">
                          Limit: ${spendingData.spendingLimit.toFixed(2)}/month
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Card>
          </Box>
        )}

        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Total Customers"
              value={safeStats.totalCustomers}
              icon={<PeopleIcon sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 10px rgba(255,255,255,0.3))' }} />}
              gradient="rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.1)"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Active Customers"
              value={safeStats.activeCustomers}
              icon={<PeopleIcon sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 10px rgba(255,255,255,0.3))' }} />}
              gradient="rgba(67, 233, 123, 0.2), rgba(56, 249, 215, 0.1)"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Today's Appointments"
              value={safeStats.upcomingAppointments}
              icon={<EventIcon sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 10px rgba(255,255,255,0.3))' }} />}
              gradient="rgba(240, 147, 251, 0.2), rgba(245, 87, 108, 0.1)"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Active Conversations"
              value={safeStats.activeConversations}
              icon={<MessageIcon sx={{ fontSize: 48, color: 'white', filter: 'drop-shadow(0 2px 10px rgba(255,255,255,0.3))' }} />}
              gradient="rgba(79, 172, 254, 0.2), rgba(0, 242, 254, 0.1)"
            />
          </Grid>
        </Grid>
        
        {totalMessages > 0 && (
          <Box mb={3}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                p: 2,
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <HistoryIcon sx={{ fontSize: 32, color: '#00D4FF' }} />
                <Box>
                  <Typography variant="h6" color="white" fontWeight="bold">
                    Total Messages: {totalMessages}
                  </Typography>
                  <Typography variant="caption" color="rgba(255,255,255,0.7)">
                    Across all active conversations
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                p: 3,
              }}
            >
              <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                Weekly Growth
              </Typography>
              <Box display="flex" alignItems="center" mt={3}>
                <TrendingUpIcon sx={{ fontSize: 60, color: '#43e97b', mr: 3 }} />
                <Box>
                  <Typography variant="h2" sx={{ 
                    color: '#43e97b',
                    fontWeight: 'bold',
                    textShadow: '0 2px 10px rgba(67, 233, 123, 0.4)',
                  }}>
                    +{safeStats.weeklyGrowth}%
                  </Typography>
                  <Typography variant="body1" color="rgba(255,255,255,0.7)" mt={1}>
                    Compared to last week
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                p: 3,
                mb: 3,
              }}
            >
              <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                Recent Activity
              </Typography>
              {recentConversations.length > 0 ? (
                <Box mt={2}>
                  {recentConversations.map((conv) => (
                    <Box
                      key={conv.conversationId}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                      onClick={() => navigate('/messages')}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" color="white" fontWeight="bold">
                            {conv.channel?.toUpperCase() || 'SMS'} Conversation
                          </Typography>
                          <Typography variant="caption" color="rgba(255,255,255,0.7)">
                            {conv.lastMessagePreview || 'No messages yet'}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="caption" color="rgba(255,255,255,0.7)">
                            {new Date(conv.lastMessageAt).toLocaleDateString()}
                          </Typography>
                          {conv.messageCount && (
                            <Typography variant="caption" color="#00D4FF" display="block">
                              {conv.messageCount} messages
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                  <Box mt={2} textAlign="center">
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate('/messages')}
                      sx={{
                        color: '#667eea',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      View All Messages →
                    </Link>
                  </Box>
                </Box>
              ) : (
                <Box mt={2} textAlign="center" py={4}>
                  <MessageIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)', mb: 2 }} />
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    No recent conversations
                  </Typography>
                  <Typography variant="caption" color="rgba(255,255,255,0.5)" display="block" mt={1}>
                    Start texting your phone number to see activity here
                  </Typography>
                </Box>
              )}
            </Card>
            
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                p: 3,
              }}
            >
              <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                Quick Actions
              </Typography>
              <Box mt={3}>
                <Link
                  component="button"
                  variant="body1"
                  onClick={() => navigate('/customers')}
                  sx={{
                    color: '#667eea',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#5568d3',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <AddIcon fontSize="small" />
                  Add a new customer
                </Link>
                {/* Only show appointments link if business type is appointment or hybrid */}
                {(vendor?.businessType === 'appointment' || vendor?.businessType === 'hybrid') && (
                  <Link
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/appointments')}
                    sx={{
                      color: '#667eea',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                      cursor: 'pointer',
                      '&:hover': {
                        color: '#5568d3',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    <EventIcon fontSize="small" />
                    Book an appointment
                  </Link>
                )}
                <Link
                  component="button"
                  variant="body1"
                  onClick={() => navigate('/agent-config')}
                  sx={{
                    color: '#667eea',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#5568d3',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <SmartToyIcon fontSize="small" />
                  Configure your AI agent
                </Link>
                <Link
                  component="button"
                  variant="body1"
                  onClick={() => navigate('/messages')}
                  sx={{
                    color: '#667eea',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#5568d3',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <HistoryIcon fontSize="small" />
                  View conversation history
                </Link>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
